import { v4 } from "uuid";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import SimpleCrud, { defaultCrudMessages } from "../../shared/crud/simple-crud";
import { SegmentEntity } from "./domain/segment.entity";
import { FormConfiguration } from "../../shared/crud/form";
import { SwitchGroup } from "../../shared/widgets/SwitchGroup";
import { defineMessage, defineMessages } from "react-intl";
import schema from "../../../gen/schema/witbooking.backoffice.orchestrator.segment.v1/Segment.json";
import { TableColumns } from "../../shared/crud/list";
import React, { useCallback, useMemo } from "react";
import { RendererProps } from "../../shared/renderers";
import { SwitchWrapper } from "../../taxation";
import SwitchComponent from "../../witadmin/CRUD/Common/SwitchGroup";
import { StringRenderer } from "../../shared/renderers/table";
import { SECTION } from "../../../lib/constants";
import { SegmentGrpcRepository } from "./infrastructure/repositories/SegmentGrpcRepository";
import { useSegments } from "./application/hooks/useSegments";
import { useSegmentMutations } from "./application/hooks/useSegmentMutations";
import { useProperty } from "../../../components/property-provider";
import TextAreaGroup from "../../witadmin/CRUD/Common/TextAreaGroup";

const segmentRepository = new SegmentGrpcRepository();

const messages = {
  ...defaultCrudMessages,
  entityName: {
    id: "orchestrator.segments.entityName",
    defaultMessage: "Segments",
  },
  tableHeaders: defineMessages({
    active: {
      id: "default.active",
      defaultMessage: "Active",
    },
    ticker: {
      id: "app.ticker",
      defaultMessage: "Ticker",
    },
    name: {
      id: "orchestrator.segment.header.name",
      defaultMessage: "Name",
    },
    description: {
      id: "orchestrator.segment.header.description",
      defaultMessage: "Description",
    },
  }),
};

const tableColumns: TableColumns<SegmentEntity> = {
  active: {
    renderer: SwitchRenderer,
    width: 100,
  },
  ticker: {
    renderer: StringRenderer,
    width: 300,
  },
  name: {
    renderer: StringRenderer,
    width: 300,
  },
  description: {
    renderer: props => (
      <StringRenderer
        {...props}
        value={props.value}
        config={{ ...props.config, maxCharacters: 500 }}
      />
    ),
  },
};

function SwitchRenderer({ value }: RendererProps<boolean>) {
  return (
    <SwitchWrapper>
      <SwitchComponent
        value={!!value}
        field={{
          className: "witadmin-bs3",
        }}
        caller=""
        entity={{}}
      />
    </SwitchWrapper>
  );
}

function Segment() {
  const propertyInfo = useProperty();
  const { refetch } = useSegments(propertyInfo.ticker, segmentRepository, { enabled: false });
  const { createSegment, updateSegment, deleteSegment } = useSegmentMutations(
    propertyInfo.ticker,
    segmentRepository
  );

  const formConfiguration: FormConfiguration<SegmentEntity> = useMemo(
    () => ({
      __fieldOrder: ["active", "id", "ticker", "name", "description", "*"],
      __useProtoStyleArrays: true,
      id: {
        hidden: true,
        default: () => v4(),
      },
      active: {
        widget: SwitchGroup,
        messages: {
          label: defineMessage({
            id: "default.active",
            defaultMessage: "Active",
          }),
        },
      },
      ticker: {
        readonly: isNew => !isNew,
        messages: {
          label: defineMessage({
            id: "app.ticker",
            defaultMessage: "Ticker",
          }),
        },
      },
      name: {
        messages: {
          label: defineMessage({
            id: "orchestrator.segment.header.name",
            defaultMessage: "Name",
          }),
        },
      },
      description: {
        widget: props => (
          <TextAreaGroup
            value={props.value}
            field={{ fieldName: "description" }}
            rows={10}
            onChange={({ event }: { event: React.ChangeEvent<HTMLInputElement> }) =>
              props.onChange(event.target.value)
            }
          />
        ),
        messages: {
          label: defineMessage({
            id: "orchestrator.segment.header.description",
            defaultMessage: "Description",
          }),
        },
      },
    }),
    []
  );

  const handleCreate = useCallback(
    async (entity: SegmentEntity) => {
      const created = await createSegment(entity);
      return created.id;
    },
    [createSegment]
  );

  const handleGetAll = useCallback(async () => {
    const result = await refetch();
    return result.data ?? [];
  }, [refetch]);

  const handleGetOne = useCallback(
    async (id: string) => {
      const segment = await segmentRepository.findById(propertyInfo.ticker, id);
      if (!segment) {
        throw new Error(`Segment with id ${id} not found`);
      }
      return segment;
    },
    [propertyInfo.ticker]
  );

  const handleUpdate = useCallback(
    async (entity: SegmentEntity) => {
      await updateSegment(entity);
    },
    [updateSegment]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteSegment(id);
    },
    [deleteSegment]
  );

  return (
    <SimpleCrud<SegmentEntity>
      managerName="orchestratorSegmentManager"
      formConfiguration={formConfiguration}
      schema={schema}
      tableColumns={tableColumns}
      section={SECTION.ORCHESTRATOR_SEGMENTS}
      onCreate={handleCreate}
      onGetAll={handleGetAll}
      onGetOne={handleGetOne}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      hideExportButton={true}
      messages={messages}
    />
  );
}

export default withAuthorizationVerifier(Segment);
